/* Page vitrine du design system naia — composants shadcn base-nova + tokens. */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Alert } from '@/components/ui/alert';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-8">
      <p className="text-[11px] font-extrabold uppercase tracking-[0.08em] text-muted-foreground mb-3">{title}</p>
      <div className="flex flex-wrap gap-3 items-start">{children}</div>
    </section>
  );
}

export default function ShadcnShowcase() {
  const [progress] = useState(65);

  return (
    <TooltipProvider>
      <div className="max-w-[800px] mx-auto px-5 py-10">
        <h1 className="text-[22px] font-extrabold text-foreground mb-1">Design System Showcase</h1>
        <p className="text-[13px] text-muted-foreground mb-8">shadcn base-nova · @base-ui/react · validation visuelle · charte unique</p>

        <Section title="Accent (bleu Naia)">
          <div className="flex flex-wrap gap-3">
            {[
              { label: 'naia-blue', bg: 'var(--naia-blue)', text: '#fff' },
              { label: 'naia-blue-deep', bg: 'var(--naia-blue-deep)', text: '#fff' },
              { label: 'naia-info-bg', bg: 'var(--naia-info-bg)', text: 'var(--naia-blue-deep)' },
              { label: 'naia-side-muted', bg: 'var(--naia-side-muted)', text: '#fff' },
              { label: 'naia-sky', bg: 'var(--naia-sky)', text: 'var(--naia-blue-deep)' },
            ].map((t) => (
              <div key={t.label} className="flex flex-col items-center gap-1">
                <div className="w-14 h-14 rounded-xl border border-border" style={{ background: t.bg }} />
                <span className="text-[10px] text-muted-foreground font-mono">{t.label}</span>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Buttons">
          <Button>Default</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="outline">Outline</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="destructive">Destructive</Button>
          <Button size="sm">Small</Button>
          <Button disabled>Disabled</Button>
        </Section>

        <Section title="Badges">
          <Badge>Default</Badge>
          <Badge variant="secondary">Secondary</Badge>
          <Badge variant="outline">Outline</Badge>
          <Badge variant="destructive">Destructive</Badge>
        </Section>

        <Section title="Avatar">
          <Avatar>
            <AvatarImage src="" alt="ED" />
            <AvatarFallback>ED</AvatarFallback>
          </Avatar>
          <Avatar>
            <AvatarFallback>LM</AvatarFallback>
          </Avatar>
          <Avatar className="w-10 h-10">
            <AvatarFallback className="text-[13px]">CB</AvatarFallback>
          </Avatar>
        </Section>

        <Section title="Card">
          <Card className="w-[280px]">
            <CardHeader>
              <CardTitle>5ᵉ B — Mathématiques</CardTitle>
              <CardDescription>8 élèves · 7 actifs</CardDescription>
            </CardHeader>
            <CardContent>
              <Progress value={progress} className="h-1.5" />
              <p className="text-xs text-muted-foreground mt-1">{progress}% progression</p>
            </CardContent>
            <CardFooter>
              <Button size="sm" variant="outline">Voir la classe</Button>
            </CardFooter>
          </Card>
        </Section>

        <Section title="Tabs">
          <Tabs defaultValue="eleves" className="w-[360px]">
            <TabsList>
              <TabsTrigger value="eleves">Élèves</TabsTrigger>
              <TabsTrigger value="devoirs">Devoirs</TabsTrigger>
              <TabsTrigger value="stats">Stats</TabsTrigger>
            </TabsList>
            <TabsContent value="eleves">
              <p className="text-[13px] text-muted-foreground pt-2">Liste des élèves de la classe.</p>
            </TabsContent>
            <TabsContent value="devoirs">
              <p className="text-[13px] text-muted-foreground pt-2">Devoirs envoyés à cette classe.</p>
            </TabsContent>
            <TabsContent value="stats">
              <p className="text-[13px] text-muted-foreground pt-2">Statistiques de progression.</p>
            </TabsContent>
          </Tabs>
        </Section>

        <Section title="Inputs">
          <Input placeholder="Nom de la classe…" className="w-[220px]" />
          <Select>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Matière" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="maths">Mathématiques</SelectItem>
              <SelectItem value="fr">Français</SelectItem>
              <SelectItem value="svt">SVT</SelectItem>
            </SelectContent>
          </Select>
          <Textarea placeholder="Description du devoir…" className="w-[280px] h-[80px]" />
        </Section>

        <Section title="Progress">
          <div className="w-[280px] flex flex-col gap-2">
            <Progress value={30} />
            <Progress value={65} />
            <Progress value={90} />
          </div>
        </Section>

        <Section title="Tooltip">
          <Tooltip>
            <TooltipTrigger>
              <Button variant="outline">Survole-moi</Button>
            </TooltipTrigger>
            <TooltipContent>Info complémentaire</TooltipContent>
          </Tooltip>
        </Section>

        <Section title="Separator">
          <div className="w-[280px]">
            <p className="text-[13px]">Section A</p>
            <Separator className="my-2" />
            <p className="text-[13px]">Section B</p>
            <Separator className="my-2" orientation="horizontal" />
            <p className="text-[13px]">Section C</p>
          </div>
        </Section>

        <Section title="Skeleton">
          <div className="flex flex-col gap-2 w-[280px]">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-10 w-full rounded-card" />
          </div>
        </Section>

        <Section title="Alert">
          <Alert className="w-[320px]">Ceci est une alerte informative.</Alert>
        </Section>

        <Section title="ScrollArea">
          <ScrollArea className="h-[120px] w-[280px] border border-border rounded-card p-3">
            {Array.from({ length: 12 }, (_, i) => (
              <p key={i} className="text-[13px] py-0.5">Élève {i + 1} — Nom Prénom</p>
            ))}
          </ScrollArea>
        </Section>
      </div>
    </TooltipProvider>
  );
}
